const POSTER_ROLES = new Set(["last_frame", "thumbnail", "poster", "cover", "preview", "first_frame"]);

type PosterAsset = Readonly<{
  kind: string;
  url?: string | null;
  downloadUrl?: string | null;
  metadata?: { role?: string; field?: string };
}>;

function token(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function stringOrUrl(value: unknown): string | undefined {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object" && !Array.isArray(value) && typeof (value as { url?: unknown }).url === "string") {
    return (value as { url: string }).url;
  }
  return undefined;
}

function imageUrl(asset: PosterAsset): string | undefined {
  if (asset.kind !== "image") return undefined;
  const url = asset.url || asset.downloadUrl;
  return typeof url === "string" && url ? url : undefined;
}

function posterFromOutput(output: unknown): string | undefined {
  if (!output || typeof output !== "object" || Array.isArray(output)) return undefined;
  const record = output as Record<string, unknown>;
  for (const key of ["last_frame_url", "poster_url", "posterUrl", "thumbnail_url", "cover_url", "first_frame_url"]) {
    const url = stringOrUrl(record[key]);
    if (url) return url;
  }
  for (const key of ["last_frame", "thumbnail", "poster", "cover"]) {
    const url = stringOrUrl(record[key]);
    if (url) return url;
  }
  const content = record.content;
  if (content && typeof content === "object" && !Array.isArray(content)) {
    const nested = content as Record<string, unknown>;
    for (const key of ["last_frame_url", "poster_url", "thumbnail_url"]) {
      const url = stringOrUrl(nested[key]);
      if (url) return url;
    }
  }
  return undefined;
}

/** Cover frame for a generated video: last_frame / thumbnail assets first, then output JSON. */
export function videoPosterUrl(input: { assets?: readonly PosterAsset[]; output?: unknown }): string | undefined {
  const assets = input.assets ?? [];
  const stills = assets.filter((asset) => asset.metadata?.role !== "input" && imageUrl(asset));
  const ranked = stills.find((asset) => POSTER_ROLES.has(token(asset.metadata?.role)) || POSTER_ROLES.has(token(asset.metadata?.field)));
  if (ranked) return imageUrl(ranked);
  return posterFromOutput(input.output) ?? (stills[0] ? imageUrl(stills[0]) : undefined);
}
